/**
 * Slide Creation — AI Crosstabs as a data source.
 *
 * Slide Creation reads AI Crosstabs ONLY. The legacy `/crosstab` tool is a
 * separate, Snowflake-backed system and is deliberately out of scope — see
 * docs/pdlc/slide-creation.md §3.1.
 *
 * AI and legacy crosstabs share one portal table, so the list is filtered with
 * `isDaniCrosstab`, which since M4S-5579 reads the dedicated `ai_layout` column
 * (surfaced on list payloads as `hasAiLayout`) and nothing else.
 */

import { crosstabLibraryService } from '@/lib/crosstab/library/list-service';
import type { CrosstabItem } from '@/lib/crosstab/library/types';
import { isDaniCrosstab, stripDaniPrefix } from '@/lib/crosstab/library/route-helpers';
import { readAiCrosstabState, type AiCrosstabState } from '@/lib/crosstab/ai-layout';

/**
 * Pages to walk when collecting a project's crosstabs. The portal list endpoint
 * paginates and offers no "everything" request, so a project with more layouts
 * than this is reported as truncated rather than silently cut short.
 */
const MAX_PAGES = 5;

export interface AiCrosstabSummary {
    /** Layout key — the crosstab's id. */
    id: string;
    /** Display name, with any legacy "DANI: " prefix stripped. */
    name: string;
    /**
     * Numeric salesforce_project_id. Typed `number` on `CrosstabItem`, but the
     * live endpoint returns it as a string — normalised here.
     */
    projectId: string;
    projectName: string;
    status: CrosstabItem['status'];
    createdAt: string;
    updatedAt: string;
    /**
     * True when the crosstab was built by an MFour user rather than a client.
     * The closest thing the portal list endpoint offers to authorship — it does
     * not return a creator name, email, or id on either the list or detail
     * response.
     */
    isInternal?: boolean;
}

export interface AiCrosstabListResult {
    items: AiCrosstabSummary[];
    /** True when the project has more pages than MAX_PAGES allowed us to read. */
    truncated: boolean;
}

/**
 * Pages to walk when collecting every project id with at least one AI
 * crosstab, across the whole org rather than one project. Higher than
 * `MAX_PAGES` since this walk competes with every project at once, not one.
 */
const PROJECT_IDS_MAX_PAGES = 20;

export interface ProjectIdsWithCrosstabsResult {
    projectIds: Set<string>;
    /**
     * Project name as it appears on that project's own crosstab rows —
     * sourced from Portal, not the Salesforce-backed `salesforceProjects`
     * catalog. Some crosstab-bearing projects (old or internally-seeded
     * test data, confirmed in practice) have no matching Salesforce record
     * at all, so this is what lets the picker still show a name for one
     * instead of silently dropping it — see `ProjectPickerModal`'s fallback
     * rows.
     */
    projectNames: Map<string, string>;
    /** True when the org has more pages than PROJECT_IDS_MAX_PAGES allowed us to read. */
    truncated: boolean;
}

function toSummary(item: CrosstabItem & { isInternal?: boolean }): AiCrosstabSummary {
    return {
        id: item.id,
        name: stripDaniPrefix(item.name) || 'Untitled Crosstab',
        projectId: String(item.projectId ?? ''),
        projectName: item.projectName,
        status: item.status,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        isInternal: item.isInternal,
    };
}

/**
 * Lists the AI Crosstabs saved against a project, most recently updated first.
 *
 * Filtering happens client-side on `hasAiLayout` rather than via the endpoint's
 * `search` param, which could only match the "DANI: " name prefix — a marker any
 * rename defeats, and one M4S-5579 retired as evidence of anything.
 */
export async function listProjectAiCrosstabs(
    projectId: string,
    surveyId?: string,
): Promise<AiCrosstabListResult> {
    const first = await crosstabLibraryService.getOrgCrosstabs({
        projectId,
        surveyId,
        page: 1,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
    });

    const collected: CrosstabItem[] = [...(first.data ?? [])];
    const totalPages = first.pagination?.totalPages ?? 1;
    const pagesToRead = Math.min(totalPages, MAX_PAGES);

    if (pagesToRead > 1) {
        const rest = await Promise.all(
            Array.from({ length: pagesToRead - 1 }, (_, i) =>
                crosstabLibraryService.getOrgCrosstabs({
                    projectId,
                    surveyId,
                    page: i + 2,
                    sortBy: 'updatedAt',
                    sortOrder: 'desc',
                }),
            ),
        );
        rest.forEach((page) => collected.push(...(page.data ?? [])));
    }

    return {
        items: collected
            .filter((item) => isDaniCrosstab({ hasAiLayout: item.hasAiLayout }))
            .map(toSummary),
        truncated: totalPages > MAX_PAGES,
    };
}

interface OrgCrosstabListRow {
    projectId?: string | number | null;
    projectName?: string | null;
    hasAiLayout?: boolean;
}

interface OrgCrosstabListPage {
    items: OrgCrosstabListRow[];
    totalPages: number;
}

/**
 * One page of the org-wide crosstab list, pre-filtered server-side to AI
 * crosstabs only (`hasAiLayout=true`) — bypasses `crosstabLibraryService.getOrgCrosstabs`
 * (whose param whitelist has no `hasAiLayout`, and lives under the read-only
 * `src/@crosstab/`, so it can't be extended) and calls the same REST route
 * directly. Filtering server-side rather than walking the full mixed list and
 * filtering client-side matters here: AI crosstabs are typically a small
 * fraction of an org's total crosstabs, so mixed into one recency-sorted
 * list they can fall past any reasonable page budget even though the true
 * AI-only result set is small enough to fit on the first page or two.
 */
async function fetchAiCrosstabProjectPage(page: number): Promise<OrgCrosstabListPage> {
    const params = new URLSearchParams({
        page: String(page),
        sortBy: 'updatedAt',
        sortOrder: 'desc',
        hasAiLayout: 'true',
    });
    const res = await fetch(`/api/studio/v1/crosstab/org/list?${params.toString()}`);
    if (!res.ok) throw new Error('Could not load the org crosstab list.');
    const json = (await res.json()) as {
        data?: { data?: OrgCrosstabListRow[]; pagination?: { totalPages?: number } };
    };
    return {
        items: json.data?.data ?? [],
        totalPages: json.data?.pagination?.totalPages ?? 1,
    };
}

/**
 * Collects every project id with at least one AI crosstab, org-wide.
 *
 * The project picker's list has no `hasCrosstabs` field to filter on itself —
 * this walks the org-wide crosstab list, scoped server-side to AI crosstabs
 * only, so the picker can hide projects with nothing to build a deck from
 * instead of showing "No crosstabs for this project yet." only after the
 * user expands one.
 */
export async function listProjectIdsWithCrosstabs(): Promise<ProjectIdsWithCrosstabsResult> {
    const first = await fetchAiCrosstabProjectPage(1);

    const collected: OrgCrosstabListRow[] = [...first.items];
    const totalPages = first.totalPages;
    const pagesToRead = Math.min(totalPages, PROJECT_IDS_MAX_PAGES);

    if (pagesToRead > 1) {
        const rest = await Promise.all(
            Array.from({ length: pagesToRead - 1 }, (_, i) => fetchAiCrosstabProjectPage(i + 2)),
        );
        rest.forEach((page) => collected.push(...page.items));
    }

    const aiItems = collected.filter((item) => isDaniCrosstab({ hasAiLayout: item.hasAiLayout }));

    const projectIds = new Set(aiItems.map((item) => String(item.projectId ?? '')).filter(Boolean));

    const projectNames = new Map<string, string>();
    for (const item of aiItems) {
        const id = String(item.projectId ?? '');
        if (id && item.projectName && !projectNames.has(id)) {
            projectNames.set(id, item.projectName);
        }
    }

    return { projectIds, projectNames, truncated: totalPages > PROJECT_IDS_MAX_PAGES };
}

/**
 * Reads one crosstab's saved AI state from `ai_layout`.
 *
 * This needs the detail endpoint because the list deliberately carries only the
 * `hasAiLayout` flag, not the blob — shipping every state object would bloat a
 * 100-row page for the one caller that wants it. So the list tells us which
 * crosstabs are AI ones, and this fetches the state for the one that was picked.
 *
 * Returns null for a crosstab with no AI state: a legacy row, or an AI row the
 * `migrations/studio/backfill-crosstab-ai-layout.sql` conversion has not reached. There is
 * deliberately no fallback to the pre-M4S-5579 `layout.config[0][0]` location —
 * see the reasoning in src/lib/crosstab/ai-layout.ts.
 */
export async function fetchAiCrosstabState(crosstabId: string): Promise<AiCrosstabState | null> {
    const record = await crosstabLibraryService.getCrosstab(crosstabId);
    return readAiCrosstabState({ aiLayout: record.aiLayout });
}

/**
 * The AI Crosstabs workspace URL for a project.
 *
 * Uses the numeric `salesforce_project_id`, which the AI route resolves to just
 * that project's surveys. A bare Salesforce account id has no survey scope and
 * is rejected by that route.
 */
export function aiCrosstabWorkspaceHref(
    projectId: string,
    opts: { projectName?: string | null; surveyId?: string | null } = {},
): string {
    const params = new URLSearchParams();
    if (opts.surveyId) params.set('surveys', opts.surveyId);
    if (opts.projectName) params.set('name', opts.projectName);
    const query = params.toString();
    return `/ai/crosstabs/project/${encodeURIComponent(projectId)}${query ? `?${query}` : ''}`;
}
